import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { FixedSizeList } from 'react-window';

import AutoSizer from 'react-virtualized-auto-sizer';

import {
    Button,
    IconButton,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import TableSortLabel from '@mui/material/TableSortLabel';

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CloseIcon from '@mui/icons-material/Close';

import { showProjectElements } from '../../../../store/actions/Store';

import CustomDialog from '../../../partials/CustomDialog';
import StyledTable from '../../../styled/Table';

import {useTheme} from '@mui/material';
import StyledTableRow from '../../../styled/TableRow';
import StyledTableCell from '../../../styled/TableCell';
import StyledIconButton from '../../../styled/IconButton';
import StyledTableSortLabel from '../../../styled/TableSortLabel';

const ROW_HEIGHT = 34;

const columns = [
    { id: 'actions', title: 'Actions', numeric: false },
    { id: 'name', title: 'Name', numeric: false }
];

const numbers = columns.filter(c => c.numeric).map(c => c.id);

export const ElementsTable = ({ rows, selected, setSelected, maxHeight }) => {
    const theme = useTheme();

    const [orderBy, setOrderBy] = useState(null);
    const [direction, setDirection] = useState('asc');
    const [filterColumn, setFilterColumn] = useState(null);
    const [filterValue, setFilterValue] = useState('');

    const [sortedFiltered, setSortedFiltered] = useState(rows || []);

    const { t } = useTranslation();

    const ElementsTableRow = ({ index, style }) => {
        const row = sortedFiltered[index];

        const handleChange = (newValue) => {
            setSelected(newValue);
        }

        return (
            <TableRow
                style={style}
                component="div"
                key={`${row.index}`}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 10fr',
                    cursor: 'pointer',
                    background: selected === row.name ? '#40B2E0' : 'transparent'
                }}
                onClick={() => handleChange(row.name)}
            >
                <StyledTableCell component="div">
                    <input 
                        type='radio'
                        name="selected"
                        id={row.name}
                        checked={selected === row.name}
                        onChange={() => handleChange(row.name)}
                    />
                </StyledTableCell>
                <TableCell component="div" sx={{color: selected === row.name ? '#fff' : 'inherit'}}>
                    {row.name || ''}
                </TableCell>
            </TableRow>
        );
    }

    const handleSort = (id) => {
        if (id === orderBy) {
            if (direction === 'asc') setDirection('desc');
            if (direction === 'desc') setDirection('asc');
        } else {
            setDirection('asc');
            setOrderBy(id);
        }
    };

    useEffect(() => {
        const filtered = filterColumn && filterValue
            ? rows.filter(
                (r) => `${r[filterColumn]}`.toLowerCase().indexOf(filterValue.toLowerCase()) >= 0
            ) : rows.map(r => r);
        const sorted = filtered;
        const dir = direction === 'asc' ? 1 : -1;
        const numeric = orderBy && numbers.includes(orderBy);
        sorted.sort((a, b) => {
            const va = a[orderBy];
            const vb = b[orderBy];
            if (va === vb) return 0;
            if (numeric) {
                const x = Number(va) || 0;
                const y = Number(vb) || 0; 
                return (x > y ? 1 : -1) * dir;
            }
            if (typeof va === 'string') {
                return va.localeCompare(vb) * dir;
            }
            return (va > vb ? 1 : -1) * dir;
        });
        setSortedFiltered(sorted);
    }, [orderBy, direction, filterColumn, filterValue, rows]);

    return (
        <TableContainer sx={{ height: '100%'}}>
            <StyledTable component="div" size="small" sx={{ height: '100%', overflow: 'hidden' }}>
                <TableHead component="div">
                    <StyledTableRow component="div" sx={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 10fr'
                    }}>
                        {columns.map((c) => (
                            <StyledTableCell
                                key={c.id}
                                component="div"
                                sx={{ display: 'flex', justifyContent: '' }}
                                align={c.numeric ? 'right' : 'left'}
                                padding="none"
                            >
                                <StyledIconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => {
                                        setFilterColumn(filterColumn === c.id ? null : c.id);
                                        setFilterValue('');
                                    }}
                                >
                                    {filterColumn === c.id ? <CloseIcon fontSize="small" /> : <FilterAltIcon fontSize="small" />}
                                </StyledIconButton>
                                {filterColumn === c.id ? (
                                    <TextField
                                        autoFocus
                                        size="small"
                                        value={filterValue}
                                        onChange={(e) => setFilterValue(e.target.value)}
                                        placeholder={t(c.title)}
                                        variant="standard"
                                        color="secondary"
                                    />
                                ) : (
                                    <StyledTableSortLabel
                                        direction={direction}
                                        active={orderBy === c.id}
                                        onClick={() => handleSort(c.id)}
                                    >
                                        <Trans>{c.title}</Trans>
                                    </StyledTableSortLabel>
                                )}
                            </StyledTableCell>
                        ))}
                    </StyledTableRow>
                </TableHead>
                <TableBody component="div" sx={{ height: '100%', overflowX: 'auto' }}>
                    {rows && rows.length > 0 ? (
                        <AutoSizer style={{
                            minHeight: 'calc(100vh - 15rem)',
                            minWidth: '520px',
                        }}>
                            {({ height, width }) => (
                                <FixedSizeList
                                    height={height}
                                    width={width}
                                    itemCount={sortedFiltered.length}
                                    itemSize={ROW_HEIGHT}
                                    overscanCount={15}
                                    style={{ overflowX: 'auto' }}
                                    children={ElementsTableRow}
                                />
                            )}
                        </AutoSizer>
                    ) : (
                        <div>
                            <Trans>List is empty</Trans>
                        </div>
                    )}
                </TableBody>
            </StyledTable>
        </TableContainer>
    );
};

export const SelectFloorPlanLayer = ({layers, onSave}) => {
    const { t } = useTranslation();

    const [rows, setRows] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const storeData = useSelector((state) => state.store.storeData);
    const texture = storeData && storeData.dxf ? storeData.dxf : null;

    const dispatch = useDispatch();

    useEffect(() => {
        if(!storeData || !texture) {
            setOpen(false);

            return;
        }

        if(!storeData.floor_plan.floor_plan_layer) {
            setOpen(true);
        }
    }, [storeData, texture])

    useEffect(() => {
        if(layers && layers.length > 0) {
            setRows(layers);
            setFetching(false);
        }
    }, [layers]);// eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = () => {
        setOpen(false);
    };

    const Buttons = ({onSave}) => {
        return (
            <Button variant='contained' onClick={() => {onSave(selected); setOpen(false);}}>
                <Trans>Save</Trans>
            </Button>
        )
    }

    return (
        <CustomDialog
            fullWidth
            maxWidth="lg"
            scroll="paper"
            open={open}
            onClose={handleClose}
            title={t('Select base layer')}
            buttons={<Buttons onSave={onSave} />}
        >
            {!fetching && rows && (
                <ElementsTable rows={rows} selected={selected} setSelected={setSelected} />
            )}
            {fetching && (
                <Typography>{t(`Loading layers`)}...</Typography>
            )}
        </CustomDialog>
    );
};
